import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef, HostBinding, Input,
  OnDestroy,
  Renderer2,
  ViewChild
} from '@angular/core';

@Component({
  selector: 'mtg-scroll-shadow',
  templateUrl: './scroll-shadow.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollShadowComponent implements AfterViewInit, OnDestroy {
  @Input()
  scrollPosition: 'inside' | 'outside' = 'outside';
  @Input()
  size: 'xs' | 's' | 'xl' = 's';
  @Input()
  color: 'background' | 'panel' = 'background';
  @Input()
  direction: 'vertical' | 'horizontal' = 'vertical';
  @ViewChild('scrollContainer', {read: ElementRef})
  scrollContainer: ElementRef;
  subscriptions: (() => void)[] = [];
  @HostBinding('class.shadow-top')
  topShadowVisible: boolean;
  @HostBinding('class.shadow-bottom')
  bottomShadowVisible: boolean;
  @HostBinding('class.shadow-left')
  leftShadowVisible: boolean;
  @HostBinding('class.shadow-right')
  rightShadowVisible: boolean;

  @HostBinding('class.scroll-shadow')
  get defaultClass(): boolean {
    return true;
  }

  @HostBinding('class.direction--vertical')
  get verticalDirectionClass(): boolean {
    return this.direction === 'vertical';
  }

  @HostBinding('class.direction--horizontal')
  get horizontalDirectionClass(): boolean {
    return this.direction === 'horizontal';
  }

  @HostBinding('class.scroll--outside')
  get scrollOutsideClass(): boolean {
    return this.scrollPosition === 'outside';
  }

  @HostBinding('class.scroll-inside')
  get scrollInsideClass(): boolean {
    return this.scrollPosition === 'inside';
  }

  @HostBinding('class.color--background')
  get colorBackground(): boolean {
    return this.color === 'background';
  }

  @HostBinding('class.color--panel')
  get colorPanel(): boolean {
    return this.color === 'panel';
  }

  @HostBinding('class.size--xs')
  get sizeXs(): boolean {
    return this.size === 'xs';
  }

  @HostBinding('class.size--s')
  get sizeS(): boolean {
    return this.size === 's';
  }

  @HostBinding('class.size--xl')
  get sizeXl(): boolean {
    return this.size === 'xl';
  }

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngAfterViewInit() {
    setTimeout(() => this.checkScroll());
    this.subscriptions.push(
      this.renderer.listen(this.scrollContainer.nativeElement, 'scroll', () => this.checkScroll())
    );
  }

  ngOnDestroy() {
    for (const unsubscribe of this.subscriptions) {
      unsubscribe();
    }
  }

  scrollTo(data: { top?: number, left?: number, behavior?: 'smooth' | 'instant' | 'auto' }): void {
    this.scrollContainer.nativeElement.scrollTo(data);
  }

  checkScroll(): void {
    if (this.direction === 'vertical') {
      this.checkVerticalScroll();
    } else if (this.direction === 'horizontal') {
      this.checkHorizontalScroll();
    }
  }

  private checkVerticalScroll(): void {
    const scrollContainer = this.scrollContainer.nativeElement;
    const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
    const canScrollTop = scrollContainer.scrollTop > 0;
    const canScrollBottom = scrollContainer.scrollTop < scrollHeight;
    if (canScrollTop) {
      if (!this.topShadowVisible) {
        this.topShadowVisible = true;
        this.cdr.markForCheck();
      }
    } else {
      if (this.topShadowVisible) {
        this.topShadowVisible = false;
        this.cdr.markForCheck();
      }
    }
    if (canScrollBottom) {
      if (!this.bottomShadowVisible) {
        this.bottomShadowVisible = true;
        this.cdr.markForCheck();
      }
    } else {
      if (this.bottomShadowVisible) {
        this.bottomShadowVisible = false;
        this.cdr.markForCheck();
      }
    }
  }

  private checkHorizontalScroll(): void {
    const scrollContainer = this.scrollContainer.nativeElement;
    const scrollWidth = scrollContainer.scrollWidth - scrollContainer.clientWidth;
    const canScrollLeft = scrollContainer.scrollLeft > 0;
    const canScrollRight = scrollContainer.scrollLeft < scrollWidth;
    if (canScrollLeft) {
      if (!this.leftShadowVisible) {
        this.leftShadowVisible = true;
        this.cdr.markForCheck();
      }
    } else {
      if (this.leftShadowVisible) {
        this.leftShadowVisible = false;
        this.cdr.markForCheck();
      }
    }
    if (canScrollRight) {
      if (!this.rightShadowVisible) {
        this.rightShadowVisible = true;
        this.cdr.markForCheck();
      }
    } else {
      if (this.rightShadowVisible) {
        this.rightShadowVisible = false;
        this.cdr.markForCheck();
      }
    }
  }
}
